import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";
import { getFromCache, setToCache } from "./cacheService";
import { 
  getGameIcon as getCentralGameIcon, 
  getGameApiBaseUrl as getCentralGameApiBaseUrl,
  getGameId
} from "../../constants/games";

export const getStates = async () => {
    try {
        const { data, error } = await supabase
            .from('states')
            .select('*')
            .order('name', { ascending: true });
        if (error) throw error;
        return data || [];
    } catch (err) {
        console.error('Error fetching states:', err);
        return [];
    }
};


export const getGlobalStats = async () => {
  const cached = getFromCache('global_stats');
  if (cached) return cached;

  const stats = {
    users: 0,
    stores: 0,
    products: 0,
    tournaments: 0,
    orders: 0
  };

  try {
    const [
      safeUsersRes,
      { count: storesCount },
      { count: productsCount },
      { count: tournamentsCount },
      { count: ordersCount }
    ] = await Promise.all([
      (supabase.rpc('get_safe_users') as any),
      supabase.from('stores').select('*', { count: 'exact', head: true }),
      supabase.from('cards').select('*', { count: 'exact', head: true }),
      supabase.from('tournaments').select('*', { count: 'exact', head: true }),
      supabase.from('orders').select('*', { count: 'exact', head: true })
    ]);

    // O contador de usuários/jogadores é obtido com precisão através da RPC get_safe_users
    if (safeUsersRes?.data && Array.isArray(safeUsersRes.data)) {
      stats.users = safeUsersRes.data.length;
    } else {
      const { count: fallbackUsersCount } = await supabase.from('users').select('*', { count: 'exact', head: true });
      stats.users = fallbackUsersCount || 0;
    }

    stats.stores = storesCount || 0;
    stats.products = productsCount || 0;
    stats.tournaments = tournamentsCount || 0;
    stats.orders = ordersCount || 0;
  } catch (e) { 
    console.warn('Error fetching global stats:', e);
  }

  setToCache('global_stats', stats);
  return stats;
};


export const getNotifications = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return [];

        const { data: profile } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', session.user.id)
          .maybeSingle();
        
        if (!profile) return [];

        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error: any) {
        console.error('Error fetching notifications:', error);
        return [];
    }
};


export const markNotificationAsRead = async (id: number | string) => {
    try {
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', id);
          
        return !error;
    } catch (error) {
        console.error('Error marking notification as read:', error);
        return false;
    }
};


export const markAllNotificationsAsRead = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return false;

        const { data: profile } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', session.user.id)
          .maybeSingle();

        if (!profile) return false;

        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('user_id', profile.id)
          .eq('is_read', false);
          
        return !error;
    } catch (error) {
        console.error('Error marking all notifications as read:', error);
        return false;
    }
};


export const getActivities = async (limit: number = 10) => {
  try {
    const { data: logs, error } = await supabase
      .from('action_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    let finalLogs = logs || [];
    if (finalLogs.length > 0) {
      const userIds = Array.from(new Set(finalLogs.map((log: any) => log.user_id).filter(Boolean)));
      if (userIds.length > 0) {
        try {
          const { data: safeUsers } = await (supabase.rpc('get_safe_users') as any)
            .select('id, username, fullname, avatar')
            .in('id', userIds);
          
          if (safeUsers && safeUsers.length > 0) {
            const userMap = safeUsers.reduce((acc: any, curr: any) => {
              acc[curr.id] = curr;
              return acc;
            }, {});
            
            finalLogs = finalLogs.map((log: any) => ({
              ...log,
              user: userMap[log.user_id] || null
            }));
          }
        } catch (err) {
          console.warn('Error fetching safe users for activities:', err);
        }
      }
    }
    
    // Flatten and safe-guard the data
    return finalLogs.map(log => {
      const username = log.user?.username || log.username || (typeof log.user === 'string' ? log.user : '');
      return {
        ...log,
        username,
        user_username: username,
        user_display_name: log.user?.fullname || log.user?.username || log.username || 'Usuário',
        user_avatar: log.user?.avatar || log.avatar,
        user_id_safe: log.user?.id || log.user_id,
        timestamp: new Date(log.created_at).toLocaleString('pt-BR', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    });
  } catch (error: any) {
    console.error('Error fetching activities:', error);
    return [];
  }
};


const getGameApiBaseUrl = (sauce?: string | null): string => {
  return getCentralGameApiBaseUrl(sauce);
};

export const getCardgames = async () => {
  const cached = getFromCache('games');
  if (cached) return cached;
  
  try {
    const { data, error } = await supabase
      .from('games')
      .select('id, name, slug, card_back_url, sauce')
      .order('name', { ascending: true });
    
    if (error) throw error;
    const formatted = (data || []).map((g: any) => {
      const isAvailable = g.sauce === 'homura' || g.sauce === 'kyubey';
      const apiBaseUrl = getGameApiBaseUrl(g.sauce);
      return {
        ...g,
        sauce: g.sauce,
        // Suporte a Homura e Kyubey (caso especial com api https://kyubey-proxy.vercel.app/)
        homura: g.sauce === 'homura',
        kyubey: g.sauce === 'kyubey',
        isAvailable,
        apiBaseUrl
      };
    });
    setToCache('games', formatted);
    return formatted;
  } catch (error) {
    console.error('Error fetching games:', error);
    return [];
  }
};


export const getFighterTags = async () => {
  try {
    const { data, error } = await supabase.from('fighter_tags').select('*').order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching fighter tags:', error);
    return [];
  }
};


export const getReviewPhrases = async (type?: 'positive' | 'negative') => {
  try {
    let query = supabase.from('review_phrases').select('*');
    if (type) query = query.eq('type', type);
    
    const { data, error } = await query.order('text', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching review phrases:', error);
    return [];
  }
};


export const saveActionLog = async (action: string, entity?: string, entityId?: string, details?: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return null;

    const { data, error } = await supabase
      .from('action_logs')
      .insert({
        user_id: profile.id,
        action,
        entity,
        entity_id: entityId,
        details
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error in saveActionLog:', error);
    return null;
  }
};


export const getActivePromoBanners = async () => {
  try {
    const { data, error } = await supabase.rpc('get_active_promo_banners');
    if (error) {
      console.warn('RPC get_active_promo_banners failed, trying fallback:', error);
      const { data: tableData, error: tableError } = await supabase
        .from('promo_banners')
        .select('*');
      if (tableError) throw tableError;
      if (tableData && tableData.length > 0) {
        return tableData;
      }
    } else if (data && data.length > 0) {
      return data;
    }
    
    // Default list if database returns empty
    return [
      {
        id: 'default-1',
        title: 'Nova Coleção Sorcery',
        description: 'Filtre boosters, decks fechados e cartas avulsas com as melhores taxas do mercado!',
        image_url: 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?auto=format&fit=crop&q=80&w=1200',
        link_url: '/busca',
        cta_text: 'Ver Ofertas',
        bg_color: 'from-purple-900/40 via-slate-900/90 to-purple-950/40'
      },
      {
        id: 'default-2',
        title: 'Torneios Locais',
        description: 'Encontre competições oficiais de Yu-Gi-Oh!, Pokémon e Magic perto de você!',
        image_url: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&q=80&w=1200',
        link_url: '/torneios',
        cta_text: 'Inscrições Abertas',
        bg_color: 'from-emerald-900/40 via-slate-900/90 to-emerald-950/40'
      },
      {
        id: 'default-3',
        title: 'Venda Conosco no Cardumy',
        description: 'Venda seus cards na maior comunidade TCG do Brasil. Clique e crie seu perfil de lojista agora!',
        image_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=1200',
        link_url: '/suporte',
        cta_text: 'Começar',
        bg_color: 'from-pink-900/40 via-slate-900/90 to-pink-950/40'
      }
    ];
  } catch (error) {
    console.error('Error fetching promo banners:', error);
    return [
      {
        id: 'default-1',
        title: 'Nova Coleção Sorcery',
        description: 'Filtre boosters, decks fechados e cartas avulsas com as melhores taxas do mercado!',
        image_url: 'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?auto=format&fit=crop&q=80&w=1200',
        link_url: '/busca',
        cta_text: 'Ver Ofertas',
        bg_color: 'from-purple-900/40 via-slate-900/90 to-purple-950/40'
      },
      {
        id: 'default-2',
        title: 'Torneios Locais',
        description: 'Encontre competições oficiais de Yu-Gi-Oh!, Pokémon e Magic perto de você!',
        image_url: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&q=80&w=1200',
        link_url: '/torneios',
        cta_text: 'Inscrições Abertas',
        bg_color: 'from-emerald-900/40 via-slate-900/90 to-emerald-950/40'
      },
      {
        id: 'default-3',
        title: 'Venda Conosco no Cardumy',
        description: 'Venda seus cards na maior comunidade TCG do Brasil. Clique e crie seu perfil de lojista agora!',
        image_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=1200',
        link_url: '/suporte',
        cta_text: 'Começar',
        bg_color: 'from-pink-900/40 via-slate-900/90 to-pink-950/40'
      }
    ];
  }
};


export const getGameSets = async () => {
  const cacheKey = 'game_sets';
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    let allSets: any[] = [];
    let from = 0;
    const step = 1000;
    while (true) {
      const { data, error } = await supabase
        .from('game_sets')
        .select('*')
        .order('id', { ascending: true })
        .range(from, from + step - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allSets = allSets.concat(data);
      if (data.length < step) break;
      from += step;
    }
    setToCache(cacheKey, allSets);
    return allSets;
  } catch (error) {
    console.error('Error fetching game sets:', error);
    return [];
  }
};

export const getGameSetsByGame = async (gameId?: number | string | 'All') => {
  if (!gameId || gameId === 'All') {
    return getGameSets();
  }

  const numGameId = getGameId(gameId, isNaN(Number(gameId)) ? 0 : Number(gameId));
  if (!numGameId) {
    return [];
  }

  const cacheKey = `game_sets_${numGameId}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('game_sets')
      .select('*')
      .eq('game_id', numGameId)
      .order('release_date', { ascending: false, nullsFirst: false })
      .limit(5000);
    if (error) throw error;
    setToCache(cacheKey, data || []);
    return data || [];
  } catch (error) {
    console.error(`Error fetching game sets for game ${numGameId}:`, error);
    return [];
  }
};

export const getGameSetById = async (setIdOrCodeOrSlug: number | string, gameId?: number | string) => {
  if (!setIdOrCodeOrSlug) return null;
  const cacheKey = `game_set_single_${setIdOrCodeOrSlug}_${gameId || 'all'}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    let query = supabase.from('game_sets').select('*');
    const num = Number(setIdOrCodeOrSlug);
    if (!isNaN(num) && num > 0) {
      query = query.or(`id.eq.${num},code.eq.${setIdOrCodeOrSlug},slug.eq.${setIdOrCodeOrSlug}`);
    } else {
      query = query.or(`code.eq.${setIdOrCodeOrSlug},slug.eq.${setIdOrCodeOrSlug}`);
    }

    if (gameId && gameId !== 'All') {
      const numGameId = getGameId(gameId, isNaN(Number(gameId)) ? 0 : Number(gameId));
      if (numGameId) {
        query = query.eq('game_id', numGameId);
      }
    }

    const { data, error } = await query.limit(1);
    if (error) throw error;
    let result = data && data.length > 0 ? data[0] : null;

    // Fallback: se não encontrou com game_id (ex: gameId stale ou incorreto), tenta pelo ID numérico primário
    if (!result && !isNaN(num) && num > 0) {
      const { data: fallbackById } = await supabase.from('game_sets').select('*').eq('id', num).limit(1);
      if (fallbackById && fallbackById.length > 0) {
        result = fallbackById[0];
      }
    }

    if (result) setToCache(cacheKey, result);
    return result;
  } catch (err) {
    console.error('Error fetching single set:', err);
    return null;
  }
};


export const getShippingMethods = async () => {
  try {
    const { data, error } = await supabase
      .from('shipping_methods')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching shipping methods:', error);
    return [];
  }
};


export const getPaymentMethods = async () => {
  try {
    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    return [];
  }
};


